import posthog from 'posthog-js'
import { Sentry } from '@/lib/sentry'

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://posthog.beoflow.com'

let initialized = false

/**
 * Only a PostHog *project* key (`phc_`) authenticates the browser ingest endpoints.
 * A personal API key (`phx_`) is accepted by the env var but rejected by PostHog on
 * every request: `/e/` and `/flags/` answer 401 ("API key is not valid:
 * personal_api_key"), and posthog-js retries forever.
 *
 * That is not merely lost analytics. In the iOS Lens WKWebView the native bridge
 * treats any 401 from page JS as a stale session and reloads the web view, so a
 * mistyped key turns into an endless reload loop on a shipped app that cannot be
 * patched without an App Store release (2026-07-23). Fail closed instead: no key,
 * no requests, no loop.
 */
function isProjectKey(key: string): boolean {
  return key.startsWith('phc_')
}

export function initPostHog(): void {
  if (initialized || !POSTHOG_KEY) return
  if (!isProjectKey(POSTHOG_KEY)) {
    console.error(
      'PostHog disabled: VITE_POSTHOG_KEY is not a project key (expected a phc_ prefix). ' +
        'Analytics are off until the deploy environment is corrected.',
    )
    return
  }
  initialized = true

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    defaults: '2025-05-24',
    // Health/fitness app: no session recording of user data.
    disable_session_recording: true,
    autocapture: false,
  })

  // Cross-source discriminator: every web-captured event carries source=web
  // (iOS registers source=ios; backend events carry source=backend).
  // Environment discriminator: local, staging and production all report into the
  // same PostHog project, so an untagged non-prod event is indistinguishable from
  // a real one and silently inflates production funnels.
  // Surface discriminator: WHERE the webapp is displayed — see resolveSurface.
  posthog.register({
    source: 'web',
    environment: resolveEnvironment(),
    surface: resolveSurface(),
  })
}

/** Deployment environment for analytics. Reuses the var Sentry already reads
 *  (Cloudflare Pages sets VITE_SENTRY_ENVIRONMENT=production), falling back to
 *  Vite's mode so a dev server reports "development", never "production".
 *
 *  Lowercased to match iOS `AppEnvironment.environment`, which lowercases its
 *  `ENVIRONMENT_NAME`. The two names have to agree exactly: they are compared
 *  against "production" to decide the distinct_id shape, and a build var set to
 *  "Production" would otherwise namespace production ids and split every
 *  athlete in two. */
function resolveEnvironment(): string {
  const raw =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
    import.meta.env.MODE ||
    'unknown'
  return raw.trim().toLowerCase()
}

/** Where this webapp is being DISPLAYED, which is not the same question as who
 *  emitted the event. `source` stays "web" for everything the webapp captures;
 *  `surface` says whether the pixels are inside a native app.
 *
 *  iOS embeds `/embed/wizard/v1` and `/embed/integrations/v1` in a WKWebView and
 *  passes `?surface=ios`. Without this, an iOS wizard walk was
 *  indistinguishable from a desktop one except by string-matching
 *  `$current_url` (measured 2026-09-11: 22 wizard events from an iOS QA walk
 *  looked like zero, because the only platform filter available was `source`).
 *
 *  A query parameter is attacker-controllable, so the value is allowlisted
 *  rather than passed through — an arbitrary string would pollute the property
 *  for every consumer of it. Anything unrecognised or absent reads as "web". */
function resolveSurface(): string {
  const allowed = ['ios', 'android', 'web']
  try {
    const raw = new URLSearchParams(window.location.search).get('surface')
    const value = raw?.trim().toLowerCase() ?? ''
    return allowed.includes(value) ? value : 'web'
  } catch {
    return 'web'
  }
}

/** The environments whose ids are namespaced. Everything else — production,
 *  a Pages preview, an unrecognised or missing environment name — keeps the
 *  bare profile id.
 *
 *  An ALLOWLIST, not `!== 'production'`, and the direction is the whole point.
 *  `resolveEnvironment()` falls back through vite.config.ts to `CF_PAGES_BRANCH`,
 *  which on the production deploy is the string "main": under a negated check, a
 *  production build that lost its VITE_SENTRY_ENVIRONMENT dashboard var would
 *  silently identify every athlete as `main:<id>`, splitting each into two
 *  people and leaving GDPR erasure — which deletes by the exact bare string —
 *  unable to reach the person it names. Nothing detects that and nothing walks
 *  it back. Failing the other way re-creates the staging/production id collision
 *  instead, which is contained to QA accounts and is visible the moment anyone
 *  looks. So an unknown name fails toward the bare id, deliberately.
 *
 *  A Pages preview belongs here too: previews run against the PRODUCTION
 *  backend, so the person really is the production person and a bare id is
 *  correct, not a concession. */
const NAMESPACED_ENVIRONMENTS = ['staging', 'development']

/**
 * PRODUCTION KEEPS THE BARE PROFILE ID and must continue to: the backend
 * identifies the same person by it (`analytics_distinct_id` in
 * core/analytics.py) and GDPR erasure deletes by it
 * (`core.analytics.delete_person`).
 *
 * Staging and development are namespaced, because every environment reports
 * into ONE PostHog project: a staging profile and a production profile with the
 * same number were one PostHog person, so a QA walk wrote into a real athlete's
 * timeline (iOS, measured 2026-09-10).
 *
 * Matches iOS `AnalyticsHelper.distinctId(for:environment:)` for every name
 * either client actually emits — same separator, same lowercase environment
 * name. The two clients identify the same human, so a one-character
 * disagreement splits every cross-platform funnel outside production (measured
 * 2026-09-11 on staging: one walk produced `staging:28` from iOS and `28` from
 * the webapp). The two deliberately differ on an UNKNOWN environment name,
 * which iOS namespaces and this does not — see NAMESPACED_ENVIRONMENTS. No such
 * name reaches both clients: iOS's "unknown" is a missing Info.plist key, the
 * webapp's is a missing build var, and neither has a counterpart on the other
 * platform to join to.
 */
export function analyticsDistinctId(profileId: number, environment: string): string {
  return NAMESPACED_ENVIRONMENTS.includes(environment)
    ? `${environment}:${profileId}`
    : String(profileId)
}

/**
 * Identify the PostHog person by the backend profile.id, and nothing else.
 *
 * distinct_id is the handle GDPR erasure deletes by (backend
 * `core.analytics.delete_person`), which is what lets profile-scoped data be
 * purged while the auth.User survives. A person keyed on auth.User.id outlives
 * its profile's deletion and stays attached to the retained account, so the
 * decoupling silently fails exactly where it has to hold.
 *
 * There is deliberately NO auth.User.id fallback. posthog-js only merges an
 * ANONYMOUS person into an identified one — identifying again from an
 * already-identified id switches distinct_id WITHOUT merging (verified in
 * posthog-js 1.399.0), so the old fallback permanently split one human into two
 * persons. That is the bug the backend removed on 2026-07-31; see
 * `analytics_distinct_id` in core/analytics.py. Staying anonymous is strictly
 * better: the anonymous person merges into profile.id the moment one exists.
 *
 * No PII (email/name) is sent — the distinct_id is itself an opaque id.
 */
export function identifyPostHogUser(profileId: number | null | undefined): void {
  if (profileId == null) {
    // Account.save() creates the Profile, so this should be unreachable for a
    // real account. Reported before the `initialized` bail-out because it is a
    // data-integrity anomaly, not an analytics event — it matters even when
    // analytics is switched off.
    Sentry.captureMessage('posthog.distinct_id_no_profile', { level: 'warning' })
    return
  }
  if (!initialized) return
  posthog.identify(analyticsDistinctId(profileId, resolveEnvironment()))
}

export function resetPostHog(): void {
  if (!initialized) return
  posthog.reset()
}

export function capturePostHogEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!initialized) return
  posthog.capture(event, properties)
}

// ---------------------------------------------------------------------------
// Feature flags
// ---------------------------------------------------------------------------

/** Local override, so a flag can be exercised in dev and in a Playwright run
 *  without a PostHog project: `localStorage.setItem('ff:notation-editor', '1')`.
 *  `ff:*` applies to every flag at once. */
function localOverride(key: string): boolean | null {
  try {
    const raw = window.localStorage.getItem(`ff:${key}`)
    return raw === null ? null : raw === '1' || raw === 'true'
  } catch {
    return null
  }
}

/** A QA build: a Cloudflare Pages preview deployment.
 *
 *  Decided by host, because the bundle is built the same way for preview and
 *  production and `import.meta.env.PROD` is true for both. Cloudflare serves
 *  previews from a branch or deployment SUBdomain of the project
 *  (`<branch>.beoflow-webapp.pages.dev`); production is `web.beoflow.com` and
 *  the bare apex `beoflow-webapp.pages.dev`, neither of which ends with the
 *  dotted suffix. So nothing here can open a gate in production. */
function isQaPreview(): boolean {
  try {
    return window.location.hostname.endsWith('.beoflow-webapp.pages.dev')
  } catch {
    return false
  }
}

/** Whether a feature is on for this person. Flags target the distinct_id, so a
 *  rollout to one coach needs no deploy. Off whenever PostHog is not
 *  initialized — a missing key must never open a gate.
 *
 *  That distinct_id is the bare profile id in production and `staging:<id>` on
 *  staging (see analyticsDistinctId), so a flag rolled out by bare profile id
 *  does NOT match that same athlete on staging. Target both forms, or use the
 *  `ff:<key>` localStorage override, which is what a staging QA pass wants
 *  anyway. */
export function isFeatureEnabled(key: string): boolean {
  const override = localOverride(key)
  if (override !== null) return override
  // `ff:*` turns every flag on (or off) at once, for a local backend with no
  // PostHog project behind it. Per-flag still wins, so one gate can be pinned
  // the other way while the rest follow the wildcard.
  //
  // Unlike the per-flag override above, the wildcard is confined to a non-
  // production build. Naming a key means you know which gate you are opening;
  // `*` opens every gate that exists now or later, so in a shipped bundle it
  // is a single console paste away from turning the whole app on. The per-flag
  // hatch keeps working everywhere, which is what a Playwright run against
  // `vite preview` would use.
  if (!import.meta.env.PROD) {
    const wildcard = localOverride('*')
    if (wildcard !== null) return wildcard
  }
  // Every gate is open on a preview. A preview exists to be exercised, and a
  // reviewer cannot exercise what is gated off — the flag would have to be
  // rolled out to their profile in PostHog first, which is a production change
  // made to test a branch. Per-flag overrides above still win, so a gate can
  // be pinned shut to compare against. (Daniel, #webapp 2026-09-06.)
  if (isQaPreview()) return true
  if (!initialized) return false
  return posthog.isFeatureEnabled(key) === true
}

/** Subscribe to flag loads. Returns the unsubscribe. */
export function onFeatureFlags(cb: () => void): () => void {
  if (!initialized) return () => {}
  return posthog.onFeatureFlags(() => cb())
}
